import { useState } from "react";
import { Linkedin, Link2, Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const PinterestIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.03-.655 2.569-.994 3.995-.283 1.195.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345c-.091.378-.293 1.194-.333 1.361-.052.22-.173.267-.4.161-1.492-.694-2.424-2.875-2.424-4.627 0-3.769 2.737-7.229 7.892-7.229 4.144 0 7.365 2.953 7.365 6.899 0 4.117-2.595 7.431-6.199 7.431-1.211 0-2.348-.63-2.738-1.373 0 0-.599 2.282-.744 2.84-.269 1.037-.997 2.334-1.484 3.126A12.013 12.013 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0Z" />
  </svg>
);

export const ShareButtons = ({ url, title, image }) => {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const btnCls = "flex items-center gap-2 rounded-full border px-4 py-2.5 text-xs font-medium transition-colors duration-300 hover:border-[var(--camel)] hover:text-[var(--camel)]";
  const btnStyle = { borderColor: "var(--line)", color: "var(--on-surface)", background: "var(--surface)" };

  return (
    <div className="mt-12 flex flex-wrap items-center gap-3 border-t pt-8" style={{ borderColor: "var(--line)" }} data-testid="share-buttons">
      <span className="text-xs font-bold uppercase" style={{ letterSpacing: "0.1em", color: "var(--muted-warm)" }}>
        ✦ {t.blog.share}
      </span>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-linkedin"
        className={btnCls}
        style={btnStyle}
      >
        <Linkedin size={15} /> LinkedIn
      </a>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${title} – ${url}`)}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-whatsapp"
        className={btnCls}
        style={btnStyle}
      >
        <WhatsAppIcon size={15} /> WhatsApp
      </a>
      <a
        href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}${image ? `&media=${encodeURIComponent(image)}` : ""}&description=${encodeURIComponent(title)}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-pinterest"
        className={btnCls}
        style={btnStyle}
      >
        <PinterestIcon size={15} /> Pinterest
      </a>
      <button onClick={copy} data-testid="share-copy" className={btnCls} style={copied ? { ...btnStyle, borderColor: "var(--gold)", color: "var(--camel)" } : btnStyle}>
        {copied ? <Check size={15} /> : <Link2 size={15} />} {copied ? t.blog.copied : "Link"}
      </button>
    </div>
  );
};
